import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEO from "@/components/SEO";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO title="Privacy Policy" noindex />
      <Header />
      <main className="container mx-auto px-4 py-20 md:py-28">
        <div className="max-w-3xl mx-auto">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4 text-center">
            Privacy Policy
          </h1>
          <p className="text-muted-foreground text-center mb-12">
            Effective Date: February 08, 2026
          </p>

          <div className="prose prose-lg max-w-none space-y-8 text-foreground/90">
            <p>
              This Privacy Policy explains how Little Big Moments Co, LLC collects, uses, and protects information in connection with Your Moment Song, a one-time custom song service for personal milestones and special moments.
            </p>
            <p>
              Your story is personal. Protecting your privacy while being transparent about how information is used is important to us.
            </p>

            <section className="space-y-3">
              <h2 className="font-heading text-2xl font-semibold text-foreground">1. Who This Service Is For</h2>
              <p>Your Moment Song is intended for use by adults purchasing a custom song for themselves or for another individual.</p>
              <p>This service is not directed to children, and children should not submit personal information directly.</p>
            </section>

            <section className="space-y-3">
              <h2 className="font-heading text-2xl font-semibold text-foreground">2. Information We Collect</h2>
              <p className="font-semibold">Information You Provide</p>
              <p>When you purchase a Your Moment Song, we may collect:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Your name and contact information</li>
                <li>Email address</li>
                <li>Names, dates, and story details you choose to share</li>
                <li>Optional pronunciation guidance or recordings</li>
                <li>Purchase and payment information</li>
              </ul>
              <p>You control what information you provide. Only the details necessary to create your song are used.</p>
            </section>

            <section className="space-y-3">
              <h2 className="font-heading text-2xl font-semibold text-foreground">3. How We Use Your Information</h2>
              <p>We use your information to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Create your custom song</li>
                <li>Deliver your completed song</li>
                <li>Communicate about your order or support requests</li>
                <li>Share relevant offers or information from our partner company, Enchanted Dragon Vacations</li>
                <li>Maintain service quality and security</li>
              </ul>
              <p>Your information is not used for behavioral tracking or sold to third parties.</p>
            </section>

            <section className="space-y-3">
              <h2 className="font-heading text-2xl font-semibold text-foreground">4. Story Privacy and Non-Sharing</h2>
              <p>Your Moment Song is built around privacy and trust.</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Your story is used only to create your song</li>
                <li>Your song and story are not shared, published, or reused</li>
                <li>Your song is not added to any public library or catalog</li>
                <li>Your content is not used as an example or for marketing without your explicit permission</li>
              </ul>
              <p>Each song is treated as a private keepsake.</p>
            </section>

            <section className="space-y-3">
              <h2 className="font-heading text-2xl font-semibold text-foreground">5. AI Assisted Creation</h2>
              <p>Some songs are created with the assistance of artificial intelligence as a creative support tool, alongside human direction, review, and curation.</p>
              <p>Personal information and story details are used only within our controlled creation process and are not used to train public or third-party AI models.</p>
              <p>All final songs remain owned by Little Big Moments Co, LLC.</p>
            </section>

            <section className="space-y-3">
              <h2 className="font-heading text-2xl font-semibold text-foreground">6. How We Share Information</h2>
              <p>We do not sell personal information.</p>
              <p>Information may be shared only in the following limited circumstances:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Within Little Big Moments Co, LLC, for internal operations and support</li>
                <li>With our partner company Enchanted Dragon Vacations, for marketing or informational purposes</li>
                <li>With trusted service providers that support payment processing, secure hosting, and digital delivery</li>
              </ul>
              <p>All shared information is limited to what is necessary and is protected by confidentiality obligations.</p>
            </section>

            <section className="space-y-3">
              <h2 className="font-heading text-2xl font-semibold text-foreground">7. Data Security</h2>
              <p>We use reasonable administrative, technical, and physical safeguards to protect your information.</p>
              <p>While no system can be guaranteed to be completely secure, we regularly review and improve our security practices.</p>
            </section>

            <section className="space-y-3">
              <h2 className="font-heading text-2xl font-semibold text-foreground">8. Data Retention</h2>
              <p>We retain personal information only as long as necessary to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Create and deliver your song</li>
                <li>Provide customer support</li>
                <li>Meet legal, accounting, or operational requirements</li>
              </ul>
              <p>You may request deletion of your information after your song has been delivered, subject to legal obligations.</p>
            </section>

            <section className="space-y-3">
              <h2 className="font-heading text-2xl font-semibold text-foreground">9. Your Rights and Choices</h2>
              <p>You may request to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Access your information</li>
                <li>Correct inaccurate information</li>
                <li>Request deletion of your data</li>
                <li>Opt out of marketing communications</li>
              </ul>
              <p>Requests can be made using the contact information below.</p>
            </section>

            <section className="space-y-3">
              <h2 className="font-heading text-2xl font-semibold text-foreground">10. International Users</h2>
              <p>If you access Your Moment Song from outside the United States, you understand that your information may be processed and stored in the United States.</p>
            </section>

            <section className="space-y-3">
              <h2 className="font-heading text-2xl font-semibold text-foreground">11. Changes to This Policy</h2>
              <p>We may update this Privacy Policy from time to time.</p>
              <p>Any updates will be posted on our website and apply going forward.</p>
            </section>

            <section className="space-y-3">
              <h2 className="font-heading text-2xl font-semibold text-foreground">12. Contact Us</h2>
              <p>If you have questions about this Privacy Policy or how your information is handled, please contact us at:</p>
              <p>
                <Link to="/contact" className="text-primary hover:underline">
                  song@yourmomentsong.com
                </Link>
              </p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Privacy;
